'use client';

/**
 * ProductSpecsCard — the product spec datasheet (full-width band).
 *
 * Renders the BFF-assembled `specifications` as a datasheet that uses the
 * horizontal space: dimensions span the top as a multi-column stat grid, then
 * composition (per-component table) and care/ownership sit side-by-side below.
 * Identity (SKU / collection / vendor) is deliberately NOT repeated here — it
 * lives in the overlay header and the Brief rail — so this card is spec sheet,
 * nothing else. Only groups that arrived render; degrades to the freetext
 * dimensions when a product has no captured specs.
 *
 * @see klyde_edge/neo_queries/assemblers/simple.py — ProductAssembler._build_specifications
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="product_specs"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas / overlay
 */

import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { text } from '@/lib/design/tokens';
import { useCardExport } from '@/lib/design/card-export';
import { cn } from '@/lib/utils/cn';
import { CardShell } from '../card-shell';
import type { ProductExploreResponse, SpecRow } from '@/lib/api/types';

const PRODUCT_EXPLORE_STRATEGY = 'graph_product_explore';

/** Label-over-value stat grid — the datasheet primitive. */
function StatGrid({ rows, className }: { rows: SpecRow[]; className?: string }) {
  return (
    <div className={cn('grid gap-x-6 gap-y-3', className ?? 'grid-cols-2 sm:grid-cols-3')}>
      {rows.map((r) => (
        <div key={r.label + r.value} className="min-w-0">
          <div className={cn(text.muted, 'truncate')} title={r.label}>{r.label}</div>
          <div className="font-semibold tabular-nums leading-tight">{r.value}</div>
        </div>
      ))}
    </div>
  );
}

function Block({ label, count, children }: { label: string; count?: number; children: React.ReactNode }) {
  return (
    <section className="min-w-0">
      <div className="mb-3 flex items-baseline justify-between">
        <div className={text.eyebrow}>{label}</div>
        {count != null && <span className={cn(text.muted, 'tabular-nums')}>{count}</span>}
      </div>
      {children}
    </section>
  );
}

export function ProductSpecsCard() {
  const { entityId, entityType } = useViewportSelection();
  const enabled = !!entityId && entityType === 'product';

  const { data, isLoading } = useStrategyData<ProductExploreResponse>(
    PRODUCT_EXPLORE_STRATEGY,
    enabled ? { semantic_id: entityId } : {},
    enabled,
  );

  const productForExport = data?.data?.product ?? null;
  useCardExport(useMemo(() => {
    const p = productForExport;
    const sp = p?.specifications;
    const parts: string[] = [];
    if (sp?.dimensions?.length) parts.push(`${sp.dimensions.length} dims`);
    if (sp?.composition?.length) parts.push(`${sp.composition.length} components`);
    if (p?.sku) parts.push(`SKU ${p.sku}`);
    return {
      label: 'Specifications',
      content_key: 'product_specs',
      surface: 'overlay' as const,
      status: (p ? 'loaded' : enabled ? 'loading' : 'idle') as 'loading' | 'loaded' | 'idle',
      query_key: enabled
        ? ['strategy-data', PRODUCT_EXPLORE_STRATEGY, { semantic_id: entityId }]
        : null,
      text: parts.join(' · ') || 'Specifications',
      json: p ? { field_count: sp?.field_count ?? 0, specifications: sp ?? null } : null,
    };
  }, [productForExport, enabled, entityId]));

  const product = data?.data?.product ?? null;

  if (isLoading && !product) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading…
      </div>
    );
  }

  if (!product) {
    return (
      <div className={cn(text.muted, 'h-full flex items-center justify-center')}>
        No product selected
      </div>
    );
  }

  const specs = product.specifications ?? null;

  const dimRows: SpecRow[] = specs?.dimensions.length
    ? specs.dimensions
    : (product.dimensions_text ? [{ label: 'Dimensions', value: product.dimensions_text }] : []);

  const ownFacts: SpecRow[] = [...(specs?.ownership ?? []).filter((r) => r.label !== 'Care')];
  if (product.lead_time) ownFacts.push({ label: 'Lead time', value: product.lead_time });
  if (product.qty_available != null) ownFacts.push({ label: 'Available', value: `${product.qty_available}` });
  const care = specs?.ownership.find((r) => r.label === 'Care')?.value;

  const hasComposition = !!specs?.composition.length;
  const hasConstruction = !!specs?.construction.length;
  const hasOwnership = ownFacts.length > 0 || !!care;
  const hasLower = hasComposition || hasConstruction || hasOwnership;

  if (!dimRows.length && !hasLower) {
    return (
      <div className={cn(text.muted, 'h-full flex items-center justify-center')}>
        No specifications on file.
      </div>
    );
  }

  return (
    <CardShell className="h-full overflow-auto p-4">
      <div className="mb-4 flex items-baseline justify-between">
        <div className={text.eyebrow}>Specifications</div>
        {specs?.field_count ? (
          <span className={cn(text.muted, 'tabular-nums')}>{specs.field_count} fields</span>
        ) : null}
      </div>

      {dimRows.length ? (
        <Block label="Dimensions" count={specs?.dimensions.length || undefined}>
          <StatGrid rows={dimRows} className="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6" />
        </Block>
      ) : null}

      {hasLower ? (
        <div className={cn(
          'grid gap-x-10 gap-y-6 lg:grid-cols-2',
          dimRows.length && 'mt-6 border-t border-border/60 pt-5',
        )}>
          {hasComposition ? (
            <Block label="Composition">
              <div className="overflow-x-auto">
                <table className={cn(text.body, 'w-full border-collapse')}>
                  <thead>
                    <tr className={cn(text.muted, 'text-left')}>
                      <th className="font-normal pr-4 pb-2">Part</th>
                      <th className="font-normal pr-4 pb-2">Material</th>
                      <th className="font-normal pr-4 pb-2">Colour</th>
                      <th className="font-normal pb-2">Finish</th>
                    </tr>
                  </thead>
                  <tbody>
                    {specs!.composition.map((r) => (
                      <tr key={r.component} className="border-t border-border/50 align-top">
                        <td className="capitalize pr-4 py-2 font-medium whitespace-nowrap">{r.component}</td>
                        <td className="pr-4 py-2">{r.material ?? <span className="text-muted-foreground/50">—</span>}</td>
                        <td className="pr-4 py-2">{r.colour ?? <span className="text-muted-foreground/50">—</span>}</td>
                        <td className="py-2">{r.finish ?? <span className="text-muted-foreground/50">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Block>
          ) : null}

          {(hasConstruction || hasOwnership) ? (
            <div className="space-y-6 min-w-0">
              {hasConstruction ? (
                <Block label="Construction">
                  <StatGrid rows={specs!.construction} />
                </Block>
              ) : null}
              {hasOwnership ? (
                <Block label="Care & ownership">
                  {ownFacts.length ? <StatGrid rows={ownFacts} /> : null}
                  {care ? (
                    <p className={cn(text.muted, 'mt-3 max-w-prose leading-relaxed')}>{care}</p>
                  ) : null}
                </Block>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </CardShell>
  );
}
